// pending-0.1.19 bump for RIM P0 flows port
/**
 * IraCoreService — dual-call IRA-detail hydration + operational-flow posts.
 *
 * Source of truth (ported, not reinvented):
 *   `C:\Projects\retail-account-opening\spfx-account-opening-v2\
 *    pcb-account-opening-v2\src\webparts\accountOpeningV2\
 *    components\screens\IraManagementScreen.tsx` (lines 302-442 — loadPlan
 *    fires `TaxPlnInq` + 3 parallel `Deposit/AcctInq` extended-element calls)
 *   `services/JxChangeService.ts` (lines 520-619 — TaxPlnInq /
 *    TaxPlnBenfSrch / TaxPlnBenfAdd / TaxPlnMod payload shapes).
 *
 * Why dual-call:
 *   jXchange (Core Director) filters AcctInq responses to the FIRST
 *   `IncXtendElemInfo.XtendElem` entry only (JHA case 16782206 — same
 *   gotcha that RCD's CdInqService solves). To hydrate the full IRA
 *   detail we fire `TaxPlnInfoInq` + `Deposit/AcctInq` (for type-60
 *   time-deposit fields) in parallel, then merge.
 *
 * Route:
 *   All calls proxy through `/api/jxrl/{*operation}` — the generic
 *   passthrough defined in `PCBJxChangeApi/Functions/JxChangeProxyFunction.cs`.
 *   We use `ApiClient.jxrl.taxPlnInfoInq`, `jxrl.taxPlnBenfInq`,
 *   `jxrl.taxPlnTranAdd`, `jxrl.taxPlnBenfAdd`, and `jxrl.post(...)` for
 *   AcctInq.
 *
 * No fixtures. No hardcoded ages / penalty % / reason codes. If Core
 * doesn't return a field, it stays `null` and the UI renders "—".
 */
import type { ApiClient } from '@retail-ops/api-client';
import type { RawJxResponse } from '@retail-ops/shared-types';
import type { IraPlanCode } from '../constants';
import { IRA_PLAN_TYPES } from '../constants';

// IRAs are AcctType '60' in Core Director (time-deposit family, same
// payload shape as CDs at '30').
export const IRA_ACCT_TYPE = '60';

// ----------------------------------------------------------------------------
// Public shape
// ----------------------------------------------------------------------------

export interface IraDetail {
  /** 10-digit plan number (Field 802) — the route param. */
  planNumber: string;
  planCode: IraPlanCode;
  planCodeRaw: string;
  planDescription: string | null;
  planType: string | null;
  inherited: boolean;

  /** CIF id of the IRA owner. Null if Core Director omitted it. */
  cifId: string | null;

  /** Owner birth date (ISO YYYY-MM-DD) — used by RMD age gate. Null if omitted. */
  ownerBirthDate: string | null;
  ownerAgeAtEoy: number | null;

  /** Current principal balance. Dollars. Null if Core omitted. */
  balance: number | null;
  /** Prior-year-end balance (RMD divisor basis). Null if omitted. */
  priorYearEndBalance: number | null;

  /** Year-to-date contributions. Null if omitted. */
  contributionYtd: number | null;
  priorYtdContribution: number | null;
  firstContribDate: string | null;

  /** Current plan-level interest rate (%). Null if omitted. */
  currentRate: number | null;
  maturityDate: string | null;
  termMonths: number | null;

  /** RMD (distribution) — purely hydrated from Core; ages stay config-driven. */
  requiredMinDistAmt: number | null;
  mandatoryDistDate: string | null;
  nextDistDate: string | null;
  lastDistDate: string | null;
  lastDistAmt: number | null;
  distFrequencyCode: string | null;

  /** Tax withholding (fed/state codes). */
  fedWithholdCode: string | null;
  stateWithholdCode: string | null;

  /** Origin-owner info (inherited IRA banner driver). */
  origOwnerName: string | null;
  origOwnerDeceaseDate: string | null;

  /** Opaque Core Director key used when re-posting plan mods. */
  actIntentKey: string | null;
}

export interface IraBeneficiaryRow {
  benfKey: string | null;
  benfCustId: string | null;
  benfName: string;
  benfDesigCode: 'P' | 'C' | 'M' | null;
  benfPctDist: number | null;
  benfBirthDate: string | null;
  benfGender: string | null;
  benfRelation: string | null;
}

// ----------------------------------------------------------------------------
// Request payload types (for the 4 operational flows)
// ----------------------------------------------------------------------------

export interface ContributionPostInput {
  custId: string;
  planNumber: string;
  planCode: string;
  /** Tax-year bucket for 5498. 4-digit year, e.g. 2026. */
  taxYear: number;
  /** Txn code — IRA_TXN_CODES deposit-kind (612 Regular, 619 Prior-Year, 614 Direct Rollover, 632 Transfer-In, 663 SEP-Employer). */
  tranCode: string;
  amount: number;
  /** 'check' | 'transfer' | 'ach' — free-form caller label; stamped into Reason. */
  fundingSource: string;
  regEDisclosureAck: boolean;
  auditUserId?: string;
}

export interface DistributionPostInput {
  custId: string;
  planNumber: string;
  planCode: string;
  /** Reason code from `/v3/config/ira-reason-codes` — never hardcoded. */
  reasonCode: string;
  /** Txn code — IRA_TXN_CODES withdrawal-kind (631 Normal, 641/651 Early, 654 Death). */
  tranCode: string;
  amount: number;
  fedWithholdPct: number;
  stateWithholdPct: number;
  /** 'check' | 'ach' | 'wire' — caller label. */
  deliveryMethod: string;
  /** Tax year for 1099-R (4 digits). */
  form1099Year: number;
  auditUserId?: string;
}

export interface RmdWithdrawalPostInput {
  custId: string;
  planNumber: string;
  planCode: string;
  rmdYear: number;
  /** Required minimum distribution amount (dollars). Caller computed from config factor. */
  calculatedAmount: number;
  /** Amount actually being distributed in this transaction. May equal calculatedAmount (full) or a smaller partial. */
  distributionAmount: number;
  mode: 'full' | 'partial';
  tranCode: string;
  fedWithholdPct: number;
  stateWithholdPct: number;
  auditUserId?: string;
}

export interface RolloverPostInput {
  custId: string;
  planNumber: string;
  planCode: string;
  /** 'direct' (trustee-to-trustee, no 60-day clock) or '60-day' (indirect, clock starts). */
  rolloverKind: 'direct' | '60-day';
  sourceInstitution: string;
  sourceAccountId?: string;
  /** Receiving IRA plan-type tag (TRAD / ROTH / SEP / SIMPLE / INHERITED). */
  receivingIraType: IraPlanCode;
  amount: number;
  /** 614 Direct Rollover deposit-side OR 632 Transfer-in. */
  tranCode: string;
  /** Withholding + 1099-R year for indirect rollover returns. */
  form1099Year: number;
  form5498Year: number;
  auditUserId?: string;
}

// ----------------------------------------------------------------------------
// Helpers (borrowed shape from legacy IraManagementScreen `txt()` / dateFmt)
// ----------------------------------------------------------------------------

function asText(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (v && typeof v === 'object') {
    const t = (v as Record<string, unknown>)['#text'];
    if (t == null) return '';
    return String(t);
  }
  return v == null ? '' : String(v);
}

function asNumber(v: unknown): number | null {
  const s = asText(v).trim();
  if (!s) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function asIsoDate(v: unknown): string | null {
  const raw = asText(v).trim();
  if (!raw) return null;
  if (raw === '0001-01-01' || raw === '00010101' || raw === '0001-01-01T00:00:00') return null;
  if (/^\d{8}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function getObj(src: unknown, key: string): Record<string, unknown> {
  if (!src || typeof src !== 'object') return {};
  const v = (src as Record<string, unknown>)[key];
  if (!v || typeof v !== 'object') return {};
  const t = (v as Record<string, unknown>)['#text'];
  if (Object.keys(v as Record<string, unknown>).length === 1 && t === null) return {};
  return v as Record<string, unknown>;
}

function planCodeFromNumber(planNumber: string): IraPlanCode {
  const prefix = planNumber.slice(0, 5);
  const hit = IRA_PLAN_TYPES.find((t) => t.prefix === prefix);
  return hit?.code ?? 'TRAD';
}

function desigCode(code: string): IraBeneficiaryRow['benfDesigCode'] {
  const c = (code || '').trim().toUpperCase();
  if (c === 'P' || c === 'PRIMARY') return 'P';
  if (c === 'C' || c === 'CONTINGENT') return 'C';
  if (c === 'M' || c === 'MONETARY') return 'M';
  return null;
}

// ----------------------------------------------------------------------------
// Service
// ----------------------------------------------------------------------------

export interface FetchIraDetailOptions {
  signal?: AbortSignal;
}

export class IraCoreService {
  public constructor(private readonly client: ApiClient) {}

  /**
   * Dual-call pattern (port of IraManagementScreen.loadPlan):
   *   Call A → `Customer/TaxPlnInfoInq` (plan metadata + customer plan record)
   *   Call B → `Deposit/AcctInq` with `x_TimeDepInfoRec` (balance / maturity / term)
   *   Call C → `Customer/TaxPlnBenfSrch` (beneficiary list)
   *
   * Partial-failure tolerant: if one call fails we still expose whatever
   * the others returned so officers can see balance even when benef lookup
   * times out.
   */
  public async fetchIraDetail(
    planNumber: string,
    opts: FetchIraDetailOptions = {},
  ): Promise<{ detail: IraDetail; beneficiaries: IraBeneficiaryRow[] }> {
    if (!planNumber) throw new Error('planNumber is required');
    const signal = opts.signal;
    const postOpts = signal ? { signal } : undefined;

    const callA = this.client.jxrl.taxPlnInfoInq(
      { AccountId: { AcctId: planNumber, AcctType: IRA_ACCT_TYPE } },
      postOpts,
    );
    const callB = this.client.jxrl.post<Record<string, unknown>, RawJxResponse>(
      'Deposit/AcctInq',
      {
        InAcctId: { AcctId: planNumber, AcctType: IRA_ACCT_TYPE },
        IncAcctInfo: true,
        IncXtendElemArray: { IncXtendElemInfo: { XtendElem: 'x_TimeDepInfoRec' } },
      },
      postOpts,
    );
    const callC = this.client.jxrl.taxPlnBenfInq(
      { AccountId: { AcctId: planNumber, AcctType: IRA_ACCT_TYPE } },
      { ...(postOpts ?? {}), queryParams: 'Count=20' },
    );

    const [rA, rB, rC] = await Promise.allSettled([callA, callB, callC]);

    const plnResp = rA.status === 'fulfilled' ? rA.value : ({} as RawJxResponse);
    const acctResp = rB.status === 'fulfilled' ? rB.value : ({} as RawJxResponse);
    const benfResp = rC.status === 'fulfilled' ? rC.value : ({} as RawJxResponse);

    const detail = IraCoreService.mergeDetail(planNumber, plnResp, acctResp);
    const beneficiaries = IraCoreService.parseBeneficiaries(benfResp);
    return { detail, beneficiaries };
  }

  /** Exposed static for testability (feed it canned jXRL fixtures). */
  public static mergeDetail(
    planNumber: string,
    plnResp: RawJxResponse,
    acctResp: RawJxResponse,
  ): IraDetail {
    const plnRec = plnResp as Record<string, unknown>;
    const plnCustRec = getObj(plnRec, 'PlnCustRec');
    const plnCust = Object.keys(plnCustRec).length > 0
      ? getObj(plnCustRec, 'PlnCust')
      : getObj(plnRec, 'PlnCust');
    const taxDist = Object.keys(plnCustRec).length > 0
      ? getObj(plnCustRec, 'TaxPlnDist')
      : getObj(plnRec, 'TaxPlnDist');
    const taxInfo = Object.keys(plnCustRec).length > 0
      ? getObj(plnCustRec, 'TaxPlnTaxInfo')
      : getObj(plnRec, 'TaxPlnTaxInfo');

    const planCode = planCodeFromNumber(planNumber);
    const planCodeRaw = asText(plnRec['PlanCode']) || asText(plnRec['PlnCode']) || asText(plnCustRec['PlnDesc']) || planCode;
    const planDescription = asText(plnCustRec['PlnDesc']) || null;
    const planType = asText(plnCustRec['PlnType']) || asText(plnRec['PlnType']) || null;

    const inherited = planCode === 'INHERITED'
      || asText(plnRec['InheritedFlag']) === 'Y'
      || plnRec['Inherited'] === true
      || Boolean(asText(plnCust['OrigOwnDeceaseDt']));

    const cifId = asText(plnRec['CustId']) || asText(plnCustRec['CustId']) || null;
    const ownerBirthDate = asIsoDate(plnCust['CustBirthDt']) ?? asIsoDate(plnCust['BirthDt']);
    const ownerAgeAtEoy = asNumber(plnCustRec['CustAgeAtEOY']);

    // AcctInq side — x_TimeDepInfoRec
    const acctRec = acctResp as Record<string, unknown>;
    const timeDepRec = getObj(acctRec, 'TimeDepAcctInqRec');
    const xTd = getObj(timeDepRec, 'x_TimeDepInfoRec');
    const tdInfo = getObj(xTd, 'TimeDepInfoRec');

    const balance = asNumber(xTd['CurBal'])
      ?? asNumber(plnCustRec['PlnCurBal']);
    const priorYearEndBalance = asNumber(plnCustRec['PriorYearEndBalance'])
      ?? asNumber(plnCust['PriorYearEndBal'])
      ?? asNumber(plnCust['PriorYEBal']);
    const currentRate = asNumber(plnCustRec['PlnAvgRate']);

    const maturityDate = asIsoDate(tdInfo['MatDt']);
    const termMonthsStr = asText(tdInfo['CDTerm']).trim();
    const termMonthsParsed = termMonthsStr ? Number.parseInt(termMonthsStr, 10) : NaN;
    const termMonths = Number.isFinite(termMonthsParsed) ? termMonthsParsed : null;

    const contributionYtd = asNumber(plnCust['CntrbAmtYTD']);
    const priorYtdContribution = asNumber(plnCust['CntrbAmtPriorYTD']);
    const firstContribDate = asIsoDate(plnCust['FirstContrbDt']);

    const requiredMinDistAmt = asNumber(taxDist['ReqMinDistAmt']);
    const mandatoryDistDate = asIsoDate(taxDist['MndtryDistDt']);
    const nextDistDate = asIsoDate(taxDist['NxtDistDt']);
    const lastDistDate = asIsoDate(taxDist['LastDistDt']);
    const lastDistAmt = asNumber(taxDist['LastDistAmt']);
    const distFrequencyCode = asText(taxDist['DistFrqCode']) || null;

    const fedWithholdCode = asText(taxInfo['PlnFedWithCode']) || null;
    const stateWithholdCode = asText(taxInfo['PlnStateWithCode']) || null;

    const origOwnerName = asText(plnCust['OrigOwnName']) || null;
    const origOwnerDeceaseDate = asIsoDate(plnCust['OrigOwnDeceaseDt']);

    const akt = plnRec['ActIntentKey'];
    const actIntentKey = typeof akt === 'string'
      ? akt
      : (akt && typeof akt === 'object' ? (asText((akt as Record<string, unknown>)['#text']) || null) : null);

    return {
      planNumber,
      planCode,
      planCodeRaw,
      planDescription,
      planType,
      inherited,
      cifId,
      ownerBirthDate,
      ownerAgeAtEoy,
      balance,
      priorYearEndBalance,
      contributionYtd,
      priorYtdContribution,
      firstContribDate,
      currentRate,
      maturityDate,
      termMonths,
      requiredMinDistAmt,
      mandatoryDistDate,
      nextDistDate,
      lastDistDate,
      lastDistAmt,
      distFrequencyCode,
      fedWithholdCode,
      stateWithholdCode,
      origOwnerName,
      origOwnerDeceaseDate,
      actIntentKey,
    };
  }

  /** Exposed static for testability — mirrors IraManagementScreen.loadBeneficiaries. */
  public static parseBeneficiaries(resp: RawJxResponse): IraBeneficiaryRow[] {
    const rec = resp as Record<string, unknown>;
    const rawArr = (rec['BenfSrchRecArray']
      ?? rec['TaxPlnBenfSrchRecArray']
      ?? rec['TaxPlnBenfSrchRec']
      ?? rec['Beneficiaries']
      ?? []) as unknown;
    const items = Array.isArray(rawArr)
      ? (rawArr as Array<Record<string, unknown>>)
      : rawArr && typeof rawArr === 'object'
        ? [rawArr as Record<string, unknown>]
        : [];

    const out: IraBeneficiaryRow[] = [];
    for (const raw of items) {
      const b = (raw['Benf'] ?? raw) as Record<string, unknown>;
      const benfKey = asText(raw['BenfKey']) || asText(b['BenfKey']) || null;
      const benfCustId = asText(b['BenfCustId']) || asText(b['BenfTaxId']) || null;
      const nameObj = (b['BenfName'] && typeof b['BenfName'] === 'object'
        && !(b['BenfName'] as Record<string, unknown>)['#text']
        ? b['BenfName']
        : {}) as Record<string, string>;
      const nameStr = nameObj.ComName
        || ((nameObj.FirstName ?? '') + ' ' + (nameObj.LastName ?? '')).trim();
      if (!benfKey && !benfCustId && !nameStr) continue;
      out.push({
        benfKey,
        benfCustId,
        benfName: nameStr || benfCustId || '(unnamed)',
        benfDesigCode: desigCode(asText(raw['BenfDesigId']) || asText(b['BenfDesigId'])),
        benfPctDist: asNumber(b['BenfPctDist']),
        benfBirthDate: asIsoDate(b['BenfBirthDt']),
        benfGender: asText(b['BenfGender']) || null,
        benfRelation: asText(b['BenfRelation']) || asText(b['BenfSpousCode']) || null,
      });
    }
    return out;
  }

  // --------------------------------------------------------------------------
  // Operational posts — every body shape lines up with legacy
  // `services/JxChangeService.ts` (TaxPlnBenfAdd fields, TaxPlnMod pattern).
  // Core Director enforces downstream validation; we don't duplicate.
  // --------------------------------------------------------------------------

  public postContribution(input: ContributionPostInput, signal?: AbortSignal): Promise<RawJxResponse> {
    const body: Record<string, unknown> = {
      CustId: input.custId,
      PlnType: 'IRA',
      PlnCode: input.planCode,
      TaxPlnTran: {
        TranCode: input.tranCode,
        TranAmt: input.amount.toFixed(2),
        TaxYear: String(input.taxYear),
        FundingSource: input.fundingSource,
        RegEDiscAck: input.regEDisclosureAck ? 'Y' : 'N',
      },
      AccountId: { AcctId: input.planNumber, AcctType: IRA_ACCT_TYPE },
    };
    return this.client.jxrl.taxPlnTranAdd(body, {
      ...(signal ? { signal } : {}),
      ...(input.auditUserId ? { auditUserId: input.auditUserId } : {}),
    });
  }

  public postDistribution(input: DistributionPostInput, signal?: AbortSignal): Promise<RawJxResponse> {
    const body: Record<string, unknown> = {
      CustId: input.custId,
      PlnType: 'IRA',
      PlnCode: input.planCode,
      TaxPlnTran: {
        TranCode: input.tranCode,
        TranAmt: input.amount.toFixed(2),
        ReasonCode: input.reasonCode,
        FedWithholdPct: input.fedWithholdPct.toFixed(2),
        StateWithholdPct: input.stateWithholdPct.toFixed(2),
        DeliveryMethod: input.deliveryMethod,
        Form1099Year: String(input.form1099Year),
      },
      AccountId: { AcctId: input.planNumber, AcctType: IRA_ACCT_TYPE },
    };
    return this.client.jxrl.taxPlnTranAdd(body, {
      ...(signal ? { signal } : {}),
      ...(input.auditUserId ? { auditUserId: input.auditUserId } : {}),
    });
  }

  public postRmdWithdrawal(input: RmdWithdrawalPostInput, signal?: AbortSignal): Promise<RawJxResponse> {
    const body: Record<string, unknown> = {
      CustId: input.custId,
      PlnType: 'IRA',
      PlnCode: input.planCode,
      TaxPlnTran: {
        TranCode: input.tranCode,
        TranAmt: input.distributionAmount.toFixed(2),
        RmdYear: String(input.rmdYear),
        RmdCalcAmt: input.calculatedAmount.toFixed(2),
        RmdMode: input.mode,
        FedWithholdPct: input.fedWithholdPct.toFixed(2),
        StateWithholdPct: input.stateWithholdPct.toFixed(2),
      },
      AccountId: { AcctId: input.planNumber, AcctType: IRA_ACCT_TYPE },
    };
    return this.client.jxrl.taxPlnTranAdd(body, {
      ...(signal ? { signal } : {}),
      ...(input.auditUserId ? { auditUserId: input.auditUserId } : {}),
    });
  }

  public postRollover(input: RolloverPostInput, signal?: AbortSignal): Promise<RawJxResponse> {
    const body: Record<string, unknown> = {
      CustId: input.custId,
      PlnType: 'IRA',
      PlnCode: input.planCode,
      TaxPlnTran: {
        TranCode: input.tranCode,
        TranAmt: input.amount.toFixed(2),
        RolloverKind: input.rolloverKind,
        SourceInstitution: input.sourceInstitution,
        ...(input.sourceAccountId ? { SourceAcctId: input.sourceAccountId } : {}),
        ReceivingIRAType: input.receivingIraType,
        Form1099Year: String(input.form1099Year),
        Form5498Year: String(input.form5498Year),
      },
      AccountId: { AcctId: input.planNumber, AcctType: IRA_ACCT_TYPE },
    };
    return this.client.jxrl.taxPlnTranAdd(body, {
      ...(signal ? { signal } : {}),
      ...(input.auditUserId ? { auditUserId: input.auditUserId } : {}),
    });
  }
}

/** Factory for callers that only have the ApiClient (no DI container). */
export function createIraCoreService(client: ApiClient): IraCoreService {
  return new IraCoreService(client);
}
